import { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { useGetOrganizationsQuery } from "@/data/queries";
import { Organization } from "@/data/types";
import { CreateOrganizationDialog } from "@/dialogs/create-organization-dialog";
import { useDialogState } from "@/hooks/use-dialog-state";
import { convertTimeFromSeconds } from "@/lib/utils";

const columns: ColumnDef<Organization>[] = [
  {
    accessorKey: "name",
    header: "Organization Name",
  },
  {
    accessorKey: "workos_organization_id",
    header: "Organization ID",
  },
  {
    accessorKey: "created_at",
    header: "Created at",
    cell: ({ row }) => {
      return (
        <div>
          {parseISO(row.original.created_at).toLocaleDateString()}
          <br />
          {parseISO(row.original.created_at).toLocaleTimeString()}
        </div>
      );
    },
  },
  {
    accessorKey: "templates_count",
    header: "SKUs",
  },
  {
    accessorKey: "tags_count",
    header: "Tags",
  },
  {
    accessorKey: "images_count",
    header: "Images",
  },
  {
    accessorKey: "videos_count",
    header: "Videos",
  },
  {
    accessorKey: "videos_duration_seconds_count",
    header: "Video Duration",
    cell: ({ row }) =>
      convertTimeFromSeconds(row.original.videos_duration_seconds_count),
  },
  {
    accessorKey: "tag_verifications_count",
    header: "Total Verif.",
  },
  {
    accessorKey: "external_tag_verifications_count",
    header: "API Verif.",
  },
  {
    id: "delete",
    header: "Delete",
    cell: ({ row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { openDialog } = useDialogState();

      return (
        <Button
          variant="ghost"
          onClick={() =>
            openDialog({
              name: "delete-organization",
              params: {
                organizationName: row.original.name,
                workOSOrganizationId: row.original.workos_organization_id,
              },
            })
          }
        >
          <Trash2 className="stroke-destructive" />
        </Button>
      );
    },
  },
];

export const Organizations = () => {
  const { data, isLoading, isError, error } = useGetOrganizationsQuery();

  if (isLoading) return <div />;

  if (isError) return <div>Error: {error.message}</div>;

  if (!data) return <div>No data</div>;

  return (
    <div>
      <div className="flex justify-between border-b pb-2">
        <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
          Organizations
        </h2>
        <CreateOrganizationDialog />
      </div>
      <DataTable columns={columns} data={data} />
    </div>
  );
};
